import { useEffect, useState } from 'react';
import { Coffee, GraduationCap, Plus, Trash2 } from 'lucide-react';
import { useListPeriodsQuery, useSetPeriodsMutation, getApiError, type Period } from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

type Row = { _id?: string; name: string; startTime: string; endTime: string; isTeaching: boolean };

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE BELL

  One per school. Basic schools ring one bell — every class moves at the same
  moment, which is the point of a bell — and periods per class would let two
  classes drift into schedules that cannot both be true. Then a teacher is due
  in two rooms at 09:40 and neither timetable can see it.

  Saved whole, not row by row
  ───────────────────────────
  A bell is read as a sequence: period 3 follows period 2. Editing it one row
  at a time leaves the sequence briefly wrong, and anybody loading the grid
  mid-edit sees a school day that does not exist. The order is simply each
  row's position in this list.

  Break and lunch are rows here too
  ─────────────────────────────────
  Marked as not taught in, so the grid keeps matching the real day and the
  slot cannot be filled with a lesson.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function BellScheduleDialog({
  open,
  onClose,
  institutionId,
}: {
  open: boolean;
  onClose: () => void;
  institutionId: string;
}) {
  /* No `= []` default: a fresh array literal every render makes the effect
     below re-run and set state forever. */
  const { data: saved } = useListPeriodsQuery(institutionId, { skip: !institutionId || !open });
  const [setPeriods, saveState] = useSetPeriodsMutation();
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (open && saved) setRows(saved.map((p: Period) => ({ ...p })));
  }, [open, saved]);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((list) => list.map((r, x) => (x === i ? { ...r, ...patch } : r)));

  const ordered = rows.every((r, i) => i === 0 || rows[i - 1]!.endTime <= r.startTime);
  const allValid = rows.every((r) => r.name.trim() && r.endTime > r.startTime);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bell schedule"
      description="One bell for the whole school. Every class moves to it."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saveState.isLoading}
            disabled={rows.length === 0 || !allValid}
            onClick={() =>
              void setPeriods({
                institutionId,
                periods: rows.map(({ _id, name, startTime, endTime, isTeaching }) => ({
                  _id,
                  name,
                  startTime,
                  endTime,
                  isTeaching,
                })),
              }).then((r) => !('error' in r) && onClose())
            }
          >
            Save schedule
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2.5">
        <div className="overflow-clip rounded-md border border-border">
          <div className="grid grid-cols-[1fr_92px_92px_118px_36px] gap-2 border-b border-border bg-muted/40 px-3 py-1.5">
            <span className={styles.label}>Period</span>
            <span className={styles.label}>From</span>
            <span className={styles.label}>To</span>
            <span className={styles.label}>Kind</span>
            <span />
          </div>

          {rows.map((r, i) => (
            <div
              key={r._id ?? `new-${i}`}
              className="grid grid-cols-[1fr_92px_92px_118px_36px] items-center gap-2 border-b border-border px-3 py-1.5 last:border-b-0"
            >
              <Input
                aria-label={`Name of period ${i + 1}`}
                value={r.name}
                maxLength={40}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              <Input
                aria-label={`Start of ${r.name}`}
                type="time"
                value={r.startTime}
                onChange={(e) => update(i, { startTime: e.target.value })}
              />
              <Input
                aria-label={`End of ${r.name}`}
                type="time"
                value={r.endTime}
                onChange={(e) => update(i, { endTime: e.target.value })}
              />
              {/* Two states, said in words rather than a bare checkbox — "is
                  teaching" on its own is ambiguous on a row called "Break". */}
              <button
                type="button"
                onClick={() => update(i, { isTeaching: !r.isTeaching })}
                aria-pressed={r.isTeaching}
                className={cn(
                  'flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors',
                  r.isTeaching
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {r.isTeaching ? <GraduationCap size={12} /> : <Coffee size={12} />}
                {r.isTeaching ? 'Lesson' : 'Break'}
              </button>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Remove ${r.name}`}
                disabled={rows.length <= 1}
                onClick={() => setRows((list) => list.filter((_, x) => x !== i))}
              >
                <Trash2 size={13} />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const last = rows[rows.length - 1];
              setRows([
                ...rows,
                { name: `Period ${rows.filter((r) => r.isTeaching).length + 1}`, startTime: last?.endTime ?? '08:00', endTime: '09:00', isTeaching: true },
              ]);
            }}
          >
            <Plus size={13} className="mr-1" />
            Add a period
          </Button>

          {/* A warning, not a refusal: a school may genuinely run a gap
              between periods, and the API only insists each one ends after it
              starts. */}
          {!ordered && (
            <p className="text-[11px] font-medium text-[hsl(var(--record-ink-warning))]">
              The times overlap or run backwards — check the order before saving.
            </p>
          )}
        </div>

        {saveState.error && (
          <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
            {getApiError(saveState.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
